import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import aiConfigService from '../../services/aiConfigService';
import { fr } from 'date-fns/locale';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useSkills } from '../../contexts/SkillsContext';
import { useOnCalls } from '../../contexts/OnCallsContext';
import { useAbsences } from '../../contexts/AbsencesContext';
import apiService from '../../services/api';
import { formatLocalDateToISO } from '../../utils/dateUtils';

const AIWeekFillDialog = ({ 
  open, 
  onClose, 
  weekData, 
  onSuccess 
}) => {
  const { technicians: allTechnicians } = useTechnicians();
  const { skills } = useSkills();
  const { onCalls, addOnCall, updateOnCall } = useOnCalls();
  const { absences } = useAbsences();
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && weekData) {
      // Charger la configuration IA depuis le serveur avant l'analyse
      loadAIConfigAndAnalyze();
    } else {
      // Reset state when dialog closes
      setAnalysisResult(null);
      setAiSuggestions(null);
      setError(null);
    }
  }, [open, weekData]);

  const loadAIConfigAndAnalyze = async () => {
    try {
      // Charger la configuration IA depuis le serveur
      console.log('🔧 Chargement de la configuration IA...');
      await aiConfigService.loadConfig();
      console.log('✅ Configuration IA chargée, début de l\'analyse...');
      
      // Puis analyser la semaine
      analyzeWeek();
    } catch (error) {
      console.error('❌ Erreur lors du chargement de la configuration IA:', error);
      // Continuer avec la configuration par défaut
      analyzeWeek();
    }
  };

  const analyzeWeek = () => {
    setIsAnalyzing(true);
    setError(null);
    
    try {
      // Identifier les compétences non couvertes
      const uncoveredSkills = [];
      const coveredSkills = [];
      
      skills.forEach(skill => {
        const isAssigned = weekData.assignments[skill.id];
        if (!isAssigned) {
          uncoveredSkills.push(skill);
        } else {
          coveredSkills.push(skill);
        }
      });

      // Identifier les techniciens disponibles pour cette semaine
      // 🔧 CORRECTION : Formatter les dates locales correctement
      const weekStartDate = formatLocalDateToISO(weekData.startDate);
      const weekEndDate = formatLocalDateToISO(weekData.endDate);
      
      const availableTechnicians = allTechnicians.filter(tech => {
        // Vérifier si le technicien a une absence sur cette période
        const hasAbsence = absences.some(abs => {
          const absStart = new Date(abs.startDate);
          const absEnd = new Date(abs.endDate);
          const weekStart = new Date(weekStartDate);
          const weekEnd = new Date(weekEndDate);
          
          const isBlocking = abs.technicianId === tech.id &&
                 tech.id &&
                 ((absStart <= weekStart && absEnd >= weekStart) ||
                  (absStart <= weekEnd && absEnd >= weekEnd) ||
                  (absStart >= weekStart && absEnd <= weekEnd));
                  
          // Vérifier si c'est une absence RECUP_ASTREINTE (non bloquante)
          if (isBlocking && abs.type === 'RECUP_ASTREINTE') {
            return false; // Ne pas bloquer pour RECUP_ASTREINTE
          }
          
          return isBlocking;
        });
        
        if (hasAbsence) return false;
        
        // Vérifier la période de repos (2 semaines avant/après)
        const hasRecentOnCall = onCalls.some(onCall => {
          if (onCall.technicianId !== tech.id) return false;
          
          const onCallStart = new Date(onCall.startDate);
          const onCallEnd = new Date(onCall.endDate);
          const weekStart = new Date(weekStartDate);
          const weekEnd = new Date(weekEndDate);
          
          // Calculer les jours entre les périodes
          const daysBefore = Math.floor((weekStart - onCallEnd) / (1000 * 60 * 60 * 24));
          const daysAfter = Math.floor((onCallStart - weekEnd) / (1000 * 60 * 60 * 24));
          
          // Repos minimum de 14 jours requis
          return (daysBefore >= 0 && daysBefore < 14) || 
                 (daysAfter >= 0 && daysAfter < 14);
        });
        
        if (hasRecentOnCall) return false;
        
        return true;
      });

      setAnalysisResult({
        uncoveredSkills,
        coveredSkills,
        availableTechnicians,
        weekStartDate,
        weekEndDate
      });
      
      // Si aucune compétence non couverte, pas besoin d'IA
      if (uncoveredSkills.length === 0) {
        setError({ type: 'info', message: 'Toutes les compétences sont déjà couvertes pour cette semaine.' });
      }
    } catch (err) {
      console.error('Erreur lors de l\'analyse:', err);
      setError({ type: 'error', message: 'Erreur lors de l\'analyse de la semaine.' });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateAISuggestions = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      // Préparer les données pour l'endpoint optimisé
      const uncoveredSkillIds = analysisResult.uncoveredSkills.map(skill => skill.id);
      
      const availableTechniciansData = analysisResult.availableTechnicians.map(tech => ({
        id: tech.id,
        name: `${tech.firstName} ${tech.lastName}`,
        firstName: tech.firstName,
        lastName: tech.lastName,
        skills: (tech.skills || []).map(s => 
          typeof s === 'object' ? (s.skillId ?? s.id) : s
        )
      }));

      if (availableTechniciansData.length === 0) {
        setError({ 
          type: 'warning', 
          message: 'Aucun technicien disponible pour cette semaine.' 
        });
        setIsGenerating(false);
        return;
      }

      // Récupérer la configuration IA actuelle
      const currentAIConfig = aiConfigService.getAPIConfig();
      console.log('🤖 Configuration IA utilisée:', currentAIConfig);

      // Appeler l'API IA spécialisée pour le remplissage de semaine
      const data = await apiService.post('/api/ai-week-fill', {
        weekData: {
          weekNumber: weekData.weekNumber,
          startDate: analysisResult.weekStartDate,
          endDate: analysisResult.weekEndDate
        },
        allTechnicians: allTechnicians.map(tech => ({
          id: tech.id,
          name: `${tech.firstName} ${tech.lastName}`,
          firstName: tech.firstName,
          lastName: tech.lastName,
          skills: (tech.skills || []).map(s => 
            typeof s === 'object' ? (s.skillId ?? s.id) : s
          )
        })),
        skills: skills.map(s => ({ 
          id: s.id, 
          name: s.name,
          color: s.color 
        })),
        absences: absences.map(abs => ({
          technicianId: abs.technicianId,
          startDate: abs.startDate,
          endDate: abs.endDate,
          type: abs.type
        })),
        allOnCalls: onCalls.map(onCall => ({
          technicianId: onCall.technicianId,
          startDate: onCall.startDate,
          endDate: onCall.endDate,
          skills: onCall.skills
        })),
        uncoveredSkills: uncoveredSkillIds,
        availableTechnicians: availableTechniciansData,
        aiConfig: currentAIConfig
      });
      
      if (!data.success || !data.result) {
        throw new Error('Réponse invalide de l\'API IA');
      }

      // Traiter les suggestions de l'IA
      const weekAssignments = data.result.assignments || [];
      
        console.log('✅ Suggestions IA reçues:', weekAssignments.length, 'affectation(s)');

      setAiSuggestions({
        assignments: weekAssignments,
        summary: data.result.summary,
        metadata: data.metadata
      });

      if (weekAssignments.length === 0) {
        setError({ 
          type: 'warning', 
          message: 'L\'IA n\'a pas pu générer de suggestions valides pour cette semaine. Vérifiez les contraintes de repos et d\'absences.' 
        });
      }
    } catch (err) {
      console.error('Erreur lors de la génération des suggestions IA:', err);
      setError({ 
        type: 'error', 
        message: `Erreur lors de la génération des suggestions : ${err.message}` 
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const applyAISuggestions = async () => {
    setIsApplying(true);
    setError(null);
    
    try {
      // Appliquer chaque suggestion
      for (const assignment of aiSuggestions.assignments) {
        // L'IA renvoie un tableau de techniciens, traiter chacun
        const assignmentTechnicians = assignment.technicians || [];
        
        for (const technicianData of assignmentTechnicians) {
          // L'IA peut renvoyer soit des IDs simples, soit des objets techniciens
          let technicianId;
          if (typeof technicianData === 'object') {
            // Si c'est un objet, extraire l'ID
            if (technicianData.technicianId) {
              // Format: "tech_15" -> extraire "15"
              const match = technicianData.technicianId.match(/tech_(\d+)/);
              technicianId = match ? parseInt(match[1]) : technicianData.technicianId;
            } else {
              technicianId = technicianData.id;
            }
          } else {
            // Si c'est un ID simple, l'utiliser directement
            technicianId = technicianData;
          }
          
          console.log('🔍 DEBUG - Application suggestion pour technicien:', { technicianData, technicianId });
          
          // Vérifier si le technicien a déjà une astreinte sur cette période
          // 🔧 CORRECTION : Extraire uniquement la partie date (YYYY-MM-DD) pour la comparaison
          // car les dates en base sont au format ISO complet (2026-01-02T17:00:00.000Z)
          const existingOnCall = onCalls.find(onCall => {
            const onCallStartDate = typeof onCall.startDate === 'string' ? onCall.startDate.split('T')[0] : onCall.startDate;
            const onCallEndDate = typeof onCall.endDate === 'string' ? onCall.endDate.split('T')[0] : onCall.endDate;
            
            return onCall.technicianId === technicianId &&
              onCallStartDate === assignment.startDate &&
              onCallEndDate === assignment.endDate;
          });

          // Récupérer les compétences spécifiques de ce technicien depuis la réponse IA
          let technicianSkills = [];
          if (typeof technicianData === 'object' && technicianData.skills) {
            // L'IA renvoie les compétences spécifiques de ce technicien
            technicianSkills = technicianData.skills;
            console.log('🔍 DEBUG - Compétences du technicien depuis l\'IA:', technicianSkills);
          } else {
            console.warn('⚠️ Aucune compétence spécifique trouvée pour le technicien, utilisation de skillsCovered');
            technicianSkills = assignment.skillsCovered || [];
          }

          if (existingOnCall) {
            // Fusionner les compétences
            const updatedSkills = [...new Set([...existingOnCall.skills, ...technicianSkills])];
            await updateOnCall(existingOnCall.id, {
              ...existingOnCall,
              skills: updatedSkills
            });
          } else {
            // Créer une nouvelle astreinte
            await addOnCall({
              technicianId: technicianId,
              startDate: assignment.startDate,
              endDate: assignment.endDate,
              skills: technicianSkills
            });
          }
        }
      }

      // Succès !
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Erreur lors de l\'application des suggestions:', err);
      setError({ 
        type: 'error', 
        message: `Erreur lors de l\'application : ${err.message}` 
      });
    } finally {
      setIsApplying(false);
    }
  };

  if (!weekData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon color="primary" />
          <Typography variant="h6">
            IA - Compléter automatiquement la semaine {weekData.weekNumber}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {format(weekData.startDate, 'dd/MM/yyyy', { locale: fr })} au{' '}
          {format(weekData.endDate, 'dd/MM/yyyy', { locale: fr })}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isAnalyzing && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress />
            <Typography>Analyse de la semaine en cours...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity={error.type} sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        {analysisResult && !isAnalyzing && (
          <>
            {/* Résumé de l'analyse */}
            <Card sx={{ mb: 2, bgcolor: 'info.light' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  📊 Analyse de la semaine
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label={`${analysisResult.coveredSkills.length} compétences couvertes`}
                    color="success"
                    size="small"
                  />
                  <Chip 
                    icon={<ErrorIcon />}
                    label={`${analysisResult.uncoveredSkills.length} compétences non couvertes`}
                    color="error"
                    size="small"
                  />
                  <Chip 
                    icon={<PersonIcon />}
                    label={`${analysisResult.availableTechnicians.length} techniciens disponibles`}
                    color="primary"
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Liste des compétences non couvertes */}
            {analysisResult.uncoveredSkills.length > 0 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Compétences à couvrir
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {analysisResult.uncoveredSkills.map(skill => (
                      <Chip 
                        key={skill.id}
                        label={skill.name}
                        size="small"
                        sx={{ 
                          bgcolor: skill.color, 
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Suggestions IA */}
            {!aiSuggestions && !isGenerating && analysisResult.uncoveredSkills.length > 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<SmartToyIcon />}
                  onClick={generateAISuggestions}
                  disabled={analysisResult.availableTechnicians.length === 0}
                >
                  Générer des suggestions IA
                </Button>
                {analysisResult.availableTechnicians.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    Aucun technicien disponible pour cette semaine
                  </Typography>
                )}
              </Box>
            )}

            {isGenerating && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                <CircularProgress />
                <Typography>L'IA génère des suggestions optimales...</Typography>
                <Typography variant="caption" color="text.secondary">
                  Prise en compte des contraintes de repos et des absences
                </Typography>
              </Box>
            )}

            {aiSuggestions && (
              <Card sx={{ mb: 2, bgcolor: 'success.light' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    🤖 Suggestions de l'IA
                  </Typography>
                  
                  {(() => {
                    // Affichage des suggestions IA
                    return null;
                  })()}
                  
                  {aiSuggestions && aiSuggestions.assignments && aiSuggestions.assignments.length > 0 ? (
                    <List>
                      {aiSuggestions.assignments.map((assignment, index) => {
                        // L'IA renvoie un tableau de techniciens, pas un seul technicianId
                        const assignmentTechnicians = assignment.technicians || [];
                        
                        // Récupérer toutes les compétences des techniciens assignés
                        const allAssignedSkills = new Set();
                        assignmentTechnicians.forEach(technicianData => {
                          // L'IA renvoie maintenant les compétences directement dans l'objet technicien
                          if (typeof technicianData === 'object' && technicianData.skills) {
                            // Utiliser les compétences directement depuis la réponse IA
                            technicianData.skills.forEach(skillId => {
                              allAssignedSkills.add(skillId);
                            });
                          } else {
                            // Fallback: récupérer depuis le profil du technicien
                            let technicianId;
                            if (typeof technicianData === 'object') {
                              if (technicianData.technicianId) {
                                const match = technicianData.technicianId.match(/tech_(\d+)/);
                                technicianId = match ? parseInt(match[1]) : technicianData.technicianId;
                              } else {
                                technicianId = technicianData.id;
                              }
                            } else {
                              technicianId = technicianData;
                            }
                            
                            const technician = allTechnicians.find(t => t.id === technicianId);
                            if (technician && technician.skills) {
                              technician.skills.forEach(skill => {
                                let skillId;
                                if (typeof skill === 'object') {
                                  skillId = skill.id || skill.skillId || skill.value || skill;
                                } else {
                                  skillId = skill;
                                }
                                allAssignedSkills.add(skillId);
                              });
                            }
                          }
                        });
                        
                        // Filtrer les valeurs invalides avant la conversion
                        const validSkillIds = Array.from(allAssignedSkills).filter(id => 
                          id !== undefined && id !== null && id !== ''
                        );
                        
                        const allAssignedSkillsStr = new Set(validSkillIds.map(id => String(id)));
                        const assignedSkills = skills.filter(s => allAssignedSkillsStr.has(String(s.id)));
                        
                        return (
                          <React.Fragment key={index}>
                            <ListItem>
                              <ListItemText
                                primary={
                                  <Typography component="span" variant="body1" sx={{ fontWeight: 'bold' }}>
                                    📅 Période du {assignment.startDate} au {assignment.endDate}
                                  </Typography>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                                secondary={
                                  <Box component="span" sx={{ display: 'block', mt: 1 }}>
                                    <Typography component="span" variant="body2" sx={{ display: 'block', mb: 1 }}>
                                      👥 Techniciens assignés ({assignmentTechnicians.length}) :
                                    </Typography>
                                    {assignmentTechnicians.map((technicianData, techIndex) => {
                                      // L'IA peut renvoyer soit des IDs simples, soit des objets techniciens
                                      let technicianId;
                                      let technicianName = '';
                                      
                                      if (typeof technicianData === 'object') {
                                        // Si c'est un objet, extraire l'ID et le nom
                                        if (technicianData.technicianId) {
                                          // Format: "tech_15" -> extraire "15"
                                          const match = technicianData.technicianId.match(/tech_(\d+)/);
                                          technicianId = match ? parseInt(match[1]) : technicianData.technicianId;
                                        } else {
                                          technicianId = technicianData.id;
                                        }
                                        
                                        // Utiliser le nom fourni par l'IA si disponible
                                        if (technicianData.technicianName) {
                                          technicianName = technicianData.technicianName;
                                        }
                                      } else {
                                        // Si c'est un ID simple, l'utiliser directement
                                        technicianId = technicianData;
                                      }
                                      
                                      // Si pas de nom fourni par l'IA, chercher dans la liste des techniciens
                                      if (!technicianName) {
                                        const technician = allTechnicians.find(t => t.id === technicianId);
                                        technicianName = technician ? `${technician.firstName} ${technician.lastName}` : `Technicien ${technicianId}`;
                                      }
                                      
                                      return (
                                        <Typography key={techIndex} component="span" variant="body2" sx={{ display: 'block', ml: 2 }}>
                                          • {technicianName}
                                        </Typography>
                                      );
                                    })}
                                    <Typography component="span" variant="body2" sx={{ display: 'block', mb: 1, mt: 1 }}>
                                      🎯 Compétences couvertes :
                                    </Typography>
                                    <Box component="span" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {assignedSkills.map(skill => (
                                        <Chip
                                          key={skill.id}
                                          label={skill.name}
                                          size="small"
                                          sx={{
                                            bgcolor: skill.color,
                                            color: 'white'
                                          }}
                                        />
                                      ))}
                                    </Box>

                                    {/* Affichage des informations de contraintes */}
                                    {assignment.constraintAnalysis && (
                                      <Box component="span" sx={{ display: 'block', mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                                          📊 Analyse des contraintes :
                                        </Typography>
                                        
                                        {/* Contraintes de repos */}
                                        {assignment.constraintAnalysis.restConstraints?.excludedTechnicians?.length > 0 && (
                                          <Typography component="span" variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                                            ⏰ Techniciens exclus (repos insuffisant) : {assignment.constraintAnalysis.restConstraints.excludedTechnicians.length}
                                          </Typography>
                                        )}
                                        
                        {/* Compétences non couvertes */}
                        {assignment.skillsNotCovered?.length > 0 && (
                          <Typography component="span" variant="body2" sx={{ display: 'block', mb: 0.5, color: 'error.main' }}>
                            ❌ Compétences non couvertes : {assignment.skillsNotCovered.map(item => {
                              // Si c'est un objet avec une propriété skill
                              if (typeof item === 'object' && item.skill) {
                                return item.skill;
                              }
                              // Si c'est un ID numérique, trouver le nom de la compétence
                              if (typeof item === 'number' || !isNaN(item)) {
                                const skill = skills.find(s => s.id === parseInt(item));
                                return skill ? skill.name : `ID:${item}`;
                              }
                              // Si c'est déjà une chaîne, l'utiliser directement
                              return item;
                            }).join(', ')}
                          </Typography>
                        )}
                                        
                                        {/* Taux de couverture */}
                                        {assignment.coverageRate !== undefined && (
                                          <Typography component="span" variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                                            📈 Taux de couverture : {assignment.coverageRate}%
                                          </Typography>
                                        )}
                                      </Box>
                                    )}

                                    {/* Affichage des alternatives */}
                            {aiSuggestions.summary?.alternatives?.length > 0 && (
                              <Box component="span" sx={{ display: 'block', mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                                  💡 Solutions alternatives :
                                </Typography>
                                {aiSuggestions.summary.alternatives.map((alt, altIndex) => (
                                  <Typography key={altIndex} component="span" variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                                    • {typeof alt === 'string' ? alt : `${alt.description || alt} - ${alt.tradeoffs || ''}`}
                                  </Typography>
                                ))}
                              </Box>
                            )}

                            {/* Affichage des contournements si c'est une solution alternative */}
                            {aiSuggestions.isAlternative && aiSuggestions.bypassRecommendations?.length > 0 && (
                              <Box component="span" sx={{ display: 'block', mt: 2, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                                <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                                  ⚠️ Contournements nécessaires (approbation manager requise) :
                                </Typography>
                                {aiSuggestions.bypassRecommendations.map((bypass, bypassIndex) => (
                                  <Box key={bypassIndex} component="span" sx={{ display: 'block', mb: 1, pl: 1, borderLeft: '3px solid', borderColor: bypass.impact === 'Élevé' ? 'error.main' : bypass.impact === 'Moyen' ? 'warning.main' : 'info.main' }}>
                                    <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 'bold' }}>
                                      {bypass.title} (Impact: {bypass.impact})
                                    </Typography>
                                    <Typography component="span" variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                                      {bypass.description}
                                    </Typography>
                                    <Typography component="span" variant="body2" sx={{ display: 'block', fontStyle: 'italic' }}>
                                      Techniciens: {bypass.technicians.map(t => `${t.name} (${t.action})`).join(', ')}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {/* Affichage des contournements par technicien dans les assignments */}
                            {assignment.technicians?.some(tech => tech.bypasses?.length > 0) && (
                              <Box component="span" sx={{ display: 'block', mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                                <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                                  ⚠️ Actions manager requises :
                                </Typography>
                                {assignment.technicians.filter(tech => tech.bypasses?.length > 0).map((tech, techIndex) => (
                                  <Box key={techIndex} component="span" sx={{ display: 'block', mb: 1 }}>
                                    <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 'bold' }}>
                                      {tech.technicianName} :
                                    </Typography>
                                    {tech.bypasses.map((bypass, bIndex) => (
                                      <Typography key={bIndex} component="span" variant="body2" sx={{ display: 'block', pl: 1, mb: 0.5 }}>
                                        • {bypass.description} → {bypass.managerAction}
                                      </Typography>
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            )}

                                    {/* Affichage des blocages */}
                                    {aiSuggestions.summary?.blockers?.length > 0 && (
                                      <Box component="span" sx={{ display: 'block', mt: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                                        <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                                          🚫 Contraintes bloquantes :
                                        </Typography>
                        {aiSuggestions.summary.blockers.map((blocker, blockerIndex) => (
                          <Typography key={blockerIndex} component="span" variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                            • {typeof blocker === 'string' ? blocker : (blocker.description || blocker.reason || JSON.stringify(blocker))}
                          </Typography>
                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index < aiSuggestions.assignments.length - 1 && <Divider />}
                          </React.Fragment>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune suggestion générée
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isGenerating || isApplying}>
          Annuler
        </Button>
        {aiSuggestions && aiSuggestions.assignments.length > 0 && (
          <>
            <Button
              variant="outlined"
              onClick={generateAISuggestions}
              disabled={isGenerating || isApplying}
              startIcon={isGenerating ? <CircularProgress size={20} /> : <SmartToyIcon />}
              sx={{ mr: 1 }}
            >
              {isGenerating ? 'Génération...' : 'Regénérer'}
            </Button>
            
            {/* Bouton Bypass si des contournements sont nécessaires */}
            {(aiSuggestions.isAlternative || 
              aiSuggestions.assignments.some(assignment => 
                assignment.technicians?.some(tech => tech.bypasses?.length > 0)
              )) ? (
              <Button
                variant="contained"
                color="warning"
                onClick={applyAISuggestions}
                disabled={isGenerating || isApplying}
                startIcon={isApplying ? <CircularProgress size={20} /> : <WarningIcon />}
              >
                {isApplying ? 'Bypass en cours...' : 'Appliquer avec Bypass'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={applyAISuggestions}
                disabled={isGenerating || isApplying}
                startIcon={isApplying ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {isApplying ? 'Application en cours...' : 'Appliquer les suggestions'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIWeekFillDialog;

